import * as React from 'react'
import { useIntl } from 'react-intl'
import { capitalize } from 'lodash'
import { CircularProgress, TableHead } from '@mui/material'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import { StudentGroupState, StudentState } from '../../../../features/types'
import EditGroupStudentDialog from './EditGroupStudentDialog'

interface Props {
    studentsData: StudentGroupState[]
    studentsInfo: StudentState[]
    isFetchingGroupStudents: boolean
    isLoadingStudentsInfo: boolean
}

const GroupStudentsTable: React.FC<Props> = ({
    studentsData = [],
    studentsInfo = [],
    isFetchingGroupStudents = false,
    isLoadingStudentsInfo = false,
}) => {
    const { formatMessage, locale } = useIntl()

    const getFullName = (userId: number) => {
        const student = studentsInfo?.find((student) => student?.id === userId)

        if (student) {
            return student?.firstName + ' ' + student?.lastName
        }
    }

    const getPhone = (userId: number) => {
        const student = studentsInfo?.find((student) => student?.id === userId)

        if (student) {
            return student?.mobile
        }
    }

    return (
        <Box>
            <TableContainer
                component={Paper}
                sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
                elevation={0}
            >
                <Table
                    sx={{ minWidth: 500 }}
                    aria-label="custom pagination table"
                >
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                            <TableCell>
                                {formatMessage({ id: 'fullName' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'phone' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'course' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'subjects' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'language' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'actions' })}
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    {studentsData.length ? (
                        <TableBody>
                            {studentsData.map((row) => (
                                <TableRow key={row.userId}>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {isLoadingStudentsInfo ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            getFullName(row.userId)
                                        )}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {isLoadingStudentsInfo ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            getPhone(row.userId)
                                        )}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {
                                            row[
                                                `courseTitle${capitalize(
                                                    locale,
                                                )}`
                                            ]
                                        }
                                    </TableCell>

                                    <TableCell>
                                        {
                                            row[
                                                `subjectsTitle${capitalize(
                                                    locale,
                                                )}`
                                            ]
                                        }
                                    </TableCell>

                                    <TableCell
                                        style={{ width: 60 }}
                                        align="right"
                                    >
                                        {row.language}
                                    </TableCell>

                                    <TableCell
                                        style={{ width: 60 }}
                                        align="right"
                                    >
                                        <EditGroupStudentDialog
                                            fullName={getFullName(row.userId)}
                                            courseSubjectId={row.courseSubjectId}
                                            courseUserEnrollId={
                                                row.userCourseEnrollId
                                            }
                                            language={row.language}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}

                        </TableBody>
                    ) : (
                        <TableBody>
                            <TableRow>
                                {!isFetchingGroupStudents && (
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                        colSpan={7}
                                    >
                                        {formatMessage({ id: 'noData' })}
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableBody>
                    )}

                </Table>
            </TableContainer>
        </Box>
    )
}

export default GroupStudentsTable
